if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.isBundlePurchaseSelected()) {
          return this.addBundleToCart(evt);
        }

        const bundleSelector = this.getBundleSelectorElement();
        // BOGO single-kit is allowed when:
        // - submitBogo({ single:true }) set bogoSingle, or
        // - the kit purchase radio is selected (not the bundle radio).
        // Only block the main ATC when the shopper still needs a 2nd build.
        const bogoNeedsSecondBuild =
          bundleSelector?.hasAttribute('data-bogo') &&
          !bundleSelector.bogoSingle &&
          !bundleSelector.querySelector('[data-bundle-input][value="kit"]:checked');
        if (bogoNeedsSecondBuild) {
          this.handleErrorMessage(bundleSelector.dataset.pickError || 'Choose your second build first.');
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const paintCart = (cartResponse) => {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(cartResponse);
              });
            };

            const ensureSectionsThenPaint = (cartResponse) => {
              if (cartResponse?.sections?.['cart-drawer'] || !this.cart) {
                paintCart(cartResponse);
                return Promise.resolve();
              }

              // iOS Safari / some ATC responses omit section HTML — refetch before paint
              // so the drawer does not open as a blank white panel.
              return this.fetchCartSections()
                .then((sectionsData) => {
                  paintCart({
                    ...cartResponse,
                    sections: this.normalizeCartRenderState(sectionsData).sections,
                  });
                })
                .catch(() => {
                  paintCart(cartResponse);
                });
            };

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    ensureSectionsThenPaint(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              ensureSectionsThenPaint(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            // Do not strip is-empty here — renderContents toggles it after a
            // successful paint. Clearing early opens a blank white drawer on iOS.
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getBundleSelectorElement() {
        if (this.dataset.sectionId) {
          return document.querySelector(
            `product-bundle-selector[data-section-id="${this.dataset.sectionId}"]`
          );
        }

        return document.querySelector(`product-bundle-selector[data-form-id="${this.form.id}"]`);
      }

      isBundlePurchaseSelected() {
        const bundleSelector = this.getBundleSelectorElement();
        if (!bundleSelector) return false;

        return bundleSelector.querySelector('[data-bundle-input][value="bundle"]:checked') !== null;
      }

      hasConfiguredBundleAddons(bundleSelector) {
        if (!bundleSelector) return false;
        if (bundleSelector.dataset.hasBundleAddons === 'true') return true;

        return this.getBundleAddonVariants(bundleSelector).length > 0;
      }

      getFormQuantity() {
        const quantityInput =
          this.form.querySelector('[name="quantity"]') ||
          document.querySelector(`[name="quantity"][form="${this.form.id}"]`);

        return parseInt(quantityInput?.value, 10) || 1;
      }

      getBundleAddonVariants(bundleSelector) {
        if (typeof bundleSelector.getSelectedAddons === 'function') {
          return bundleSelector.getSelectedAddons();
        }

        return [];
      }

      getBundleDiscountPercent(bundleSelector) {
        return parseFloat(bundleSelector.dataset.bundleDiscountPercent) || 0;
      }

      getCurrentKitPrice(bundleSelector) {
        if (typeof bundleSelector.currentKitPrice === 'number') {
          return bundleSelector.currentKitPrice;
        }

        return parseInt(bundleSelector.dataset.initialKitPrice, 10) || 0;
      }

      getDiscountedBundleTotal(subtotal, discountPercent) {
        if (discountPercent <= 0) return subtotal;

        const discount = Math.round((subtotal * discountPercent) / 100);
        const discounted = subtotal - discount;

        return Math.round(discounted / 100) * 100;
      }

      allocateBundleLineDiscounts(lineTotals, totalDiscount) {
        if (totalDiscount <= 0 || lineTotals.length === 0) return lineTotals.map(() => 0);

        const subtotal = lineTotals.reduce((sum, amount) => sum + amount, 0);
        if (subtotal <= 0) return lineTotals.map(() => 0);

        let allocated = 0;

        return lineTotals.map((amount, index) => {
          if (index === lineTotals.length - 1) {
            return totalDiscount - allocated;
          }

          const share = Math.round(totalDiscount * (amount / subtotal));
          allocated += share;
          return share;
        });
      }

      buildBundleCartItems(bundleSelector) {
        const variantId = parseInt(this.form.querySelector('[name="id"]')?.value, 10);
        const quantity = this.getFormQuantity();
        const purchasedAs = bundleSelector.querySelector('[data-bundle-property]')?.value || 'Bundle';
        const bundleId = `bundle-${Date.now()}`;
        const discountPercent = this.getBundleDiscountPercent(bundleSelector);
        const kitUnitPrice = this.getCurrentKitPrice(bundleSelector);
        const addons = this.getBundleAddonVariants(bundleSelector).filter((addon) => addon?.id);

        if (!variantId) return [];

        const lineTotals = [kitUnitPrice * quantity, ...addons.map((addon) => (addon.price || 0) * quantity)];
        const subtotal = lineTotals.reduce((sum, amount) => sum + amount, 0);
        const discountedTotal = this.getDiscountedBundleTotal(subtotal, discountPercent);
        const totalDiscount = subtotal - discountedTotal;
        const lineDiscounts = this.allocateBundleLineDiscounts(lineTotals, totalDiscount);

        const bundleProperties = (lineDiscountCents) => {
          const properties = {
            _bundle: bundleId,
            'Purchased as': purchasedAs,
          };

          if (discountPercent > 0) {
            properties._bundle_discount_percent = String(discountPercent);
          }

          if (lineDiscountCents > 0) {
            properties._bundle_line_discount = String(lineDiscountCents);
          }

          return properties;
        };

        const items = [
          {
            id: variantId,
            quantity,
            properties: bundleProperties(lineDiscounts[0] || 0),
          },
        ];

        addons.forEach((addon, index) => {
          items.push({
            id: parseInt(addon.id, 10),
            quantity,
            properties: {
              ...bundleProperties(lineDiscounts[index + 1] || 0),
              _bundle_addon: 'true',
            },
          });
        });

        return items;
      }

      fetchCartSections() {
        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = `${window.location.pathname}${window.location.search}`;
        const joiner = sectionsUrl.includes('?') ? '&' : '?';

        return fetch(`${sectionsUrl}${joiner}sections=${sectionIds}`).then((response) => response.json());
      }

      normalizeCartRenderState(response) {
        if (response?.sections) return response;

        if (response && (response['cart-drawer'] || response['cart-icon-bubble'])) {
          return { sections: response };
        }

        if (
          response &&
          (response['cart-notification-product'] ||
            response['cart-notification-button'] ||
            response['cart-icon-bubble'])
        ) {
          return { sections: response };
        }

        return { sections: response || {} };
      }

      prepareCartRenderResponse(response, cartItems) {
        const normalized = this.normalizeCartRenderState(response);
        const addedItems = normalized.items || response?.items;
        const mainVariantId = cartItems?.[0]?.id;
        const mainAddedItem =
          addedItems?.find(
            (item) => item.variant_id === mainVariantId || item.id === mainVariantId
          ) || addedItems?.[0];

        if (!normalized.key) {
          normalized.key = response?.key || mainAddedItem?.key;
        }

        if (!normalized.id) {
          normalized.id =
            response?.id ||
            response?.variant_id ||
            mainAddedItem?.variant_id ||
            mainAddedItem?.id ||
            mainVariantId;
        }

        return normalized;
      }

      addBundleItemsRequest(items, includeSections = false) {
        const payload = items.length === 1 ? { ...items[0] } : { items };

        if (includeSections && this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = `${window.location.pathname}${window.location.search}`;
        }

        return fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        }).then((response) => response.json());
      }

      async addBundleItemsSequentially(items) {
        let lastResponse = null;

        for (let index = 0; index < items.length; index += 1) {
          const includeSections = index === items.length - 1;
          lastResponse = await this.addBundleItemsRequest([items[index]], includeSections);

          if (lastResponse.status) {
            throw new Error(
              lastResponse.description || lastResponse.message || 'Could not add bundle to cart'
            );
          }
        }

        return lastResponse;
      }

      async addBundleItemsToCart(items) {
        const bulkResponse = await this.addBundleItemsRequest(items, true);
        let response = bulkResponse.status ? await this.addBundleItemsSequentially(items) : bulkResponse;

        if (response.status) {
          throw new Error(response.description || response.message || 'Could not add bundle to cart');
        }

        if (!this.cart) return this.prepareCartRenderResponse(response, items);

        if (!response.sections) {
          const sectionsData = await this.fetchCartSections();
          response = {
            ...response,
            sections: this.normalizeCartRenderState(sectionsData).sections,
          };
        }

        return this.prepareCartRenderResponse(response, items);
      }

      addBundleToCart(evt) {
        const bundleSelector = this.getBundleSelectorElement();

        if (!bundleSelector) {
          this.handleErrorMessage('Bundle selector is not connected to this product form.');
          return;
        }

        if (!this.hasConfiguredBundleAddons(bundleSelector)) {
          this.handleErrorMessage(
            'No bundle products are configured for this product. Add the custom.bundle_products metafield on this product in Shopify admin.'
          );
          return;
        }

        const items = this.buildBundleCartItems(bundleSelector);
        const spinner = this.querySelector('.loading__spinner');
        const variantId = items[0]?.id;

        if (items.length < 2) {
          const pickError =
            bundleSelector.dataset.pickError || 'Choose your second kit before adding the bundle.';
          this.handleErrorMessage(pickError);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: variantId,
            errors: pickError,
            message: pickError,
          });
          return;
        }

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        spinner?.classList.remove('hidden');

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, items[0]?.quantity || 1);

        this.addBundleItemsToCart(items)
          .then((response) => {
            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            this.error = false;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          })
          .catch((error) => {
            console.error(error);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: error.message,
              message: error.message,
            });
            this.handleErrorMessage(error.message);
            this.dispatchCartErrorEvent(error.message || 'Network error', 'INVALID');
            linesUpdateDeferred?.reject(error);
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            // renderContents owns is-empty after paint — clearing early blanks iOS.
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            spinner?.classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
